import * as _ from 'underscore';
import { FormattingService } from './formatting.service';
import { Inject, Injectable } from '@angular/core';
import { IExtendedWindow } from '../../../type_definitions/datatypes';
import { TranslationService } from '../translation/translation.service';
import { Title } from '@angular/platform-browser';

/**
 * @ngdoc service
 * @name ga2.shared.services:titleService
 *
 * @description
 * With titleService we can dynamically modify the value of the page's <title> tag
 *
 */

@Injectable()
export class TitleService {
  private prefix:string;
  
  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private titleService:Title
  ) {
    /**/
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services:titleService#setPrefix
   * @methodOf ga2.shared.services:titleService
   * @description set the title prefix (will be prefixed to all titles until it is changed)
   * @param {string} hostName the hostname to base the prefix on (if absent, the current hostname will be used)
   */
  public setPrefix(hostName?:string):void {
    hostName = hostName || this.window.location.hostname;

    let symbolicHostName:string;
    if (hostName === 'localhost' || hostName === '127.0.0.1') {
      symbolicHostName = 'LOCAL';
    } else {
      // dev-ga.acslworld.com => DEV
      // dev-ga-alpha.acslworld.com => DEV-ALPHA
      // test-sso.acslworld.com => TEST-SSO
      // ga-sso-01.acslworld.com => SSO-01
      // ga.acslworld.com => ''
      symbolicHostName = hostName.replace('.acslworld.com', '').replace(/-?ga/, '').replace(/-?(.*)-?/, '$1').toUpperCase();
    }

    if (symbolicHostName) {
      symbolicHostName = `[${symbolicHostName}] `;
    }
    this.prefix = symbolicHostName + 'ACSL';
  }

  public getPrefix() {
    return this.prefix;
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services:titleService#setTitle
   * @methodOf ga2.shared.services:titleService
   * @description change the title (leaving the prefix intact)
   * @param {string|string[]} title a title, or a list of title parts (joined with ' - ')
   */
  public setTitle(title:string[] | string) {
    if (!_.isArray(title)) {
      title = [ title ];
    }

    const titles:string[] = [];
    _.each(title, (aTitle:string) => {
      if (aTitle) {
        titles.push(this.translationService.instant(aTitle));
      }
    });

    title = this.formattingService.join(this.prefix, titles, ' - ');

    setTimeout(() => {
      this.titleService.setTitle(<string>title);
    });
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * @ngdoc method
   * @name ga2.shared.services:titleService#getTitle
   * @methodOf ga2.shared.services:titleService
   * @description get the title from the documents title tag
   * @returns {string} the title
   */
  public getTitle():string {
    return this.titleService.getTitle();
  }
}